import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, get, post } from '@/lib/api/client';
import { downloadResponse } from '@/lib/download';
import { dayBounds } from '@/lib/store-time';
import type {
  AccountPayable,
  CardHistoryResult,
  GiftCardLite,
  GiftCardSearchResult,
  PurchaseReport,
  TransactionsResult,
} from './types';

/**
 * Gift cards — the whole `api/giftcards` surface, in one place.
 *
 * It replaces five legacy files (`GiftCards`, `GiftCardsSearch`,
 * `GiftCardsHistory`, `GiftCardsPurchaseHistory`, `GiftCardsHistoryModal`,
 * ~2,760 lines) that between them called the same endpoints with the same
 * payloads, each with its own loading flag and its own copy of the error
 * handling. Two of those files were a menu entry apart and differed by one
 * button.
 */

const KEY = ['gift-cards'] as const;

/** Every list on the page is 20 rows; nothing here pages deeply. */
export const PAGE_SIZE = 20;

export interface DateRange {
  start: string;
  end: string;
}

/** Both ends are inclusive here — the service compares `>=` and `<=`. */
function range({ start, end }: DateRange) {
  const { startDate, endDate } = dayBounds(start, end);
  return { StartDate: startDate, EndDate: endDate };
}

export function useAccountPayable() {
  return useQuery({
    queryKey: [...KEY, 'account-payable'],
    queryFn: () => get<AccountPayable>('giftcards/accountpayable'),
    staleTime: 60_000,
  });
}

/**
 * The card list.
 *
 * No date range: the endpoint accepts one, but what it filters on depends on
 * the activation state — `ActivatedOn` for the three "activated" options and
 * `CreatedAt` for the other two — and neither question is one anybody asks of
 * a card. You look a card up by its code, or you list the ones still holding
 * money. Omitting the dates also retires the endpoint's misleading
 * `Purchased` / `Redeemed` pair, which was computed over every transaction in
 * the window regardless of the filters it was returned beside.
 */
export function useGiftCardSearch(params: {
  query: string;
  searchType: number;
  page: number;
}) {
  const trimmed = params.query.trim();

  return useQuery({
    queryKey: [...KEY, 'search', params.searchType, trimmed, params.page],
    queryFn: () =>
      post<GiftCardSearchResult>('giftcards/search', {
        Page: params.page,
        Size: PAGE_SIZE,
        Query: trimmed,
        SearchType: params.searchType,
      }),
    staleTime: 30_000,
  });
}

export function useTransactions(params: { dates: DateRange; page: number }) {
  return useQuery({
    queryKey: [...KEY, 'transactions', params.dates, params.page],
    queryFn: () =>
      post<TransactionsResult>('giftcards/history', {
        Page: params.page,
        Size: PAGE_SIZE,
        ...range(params.dates),
      }),
    staleTime: 30_000,
  });
}

export function usePurchases(params: {
  dates: DateRange;
  businessId: string | null;
  page: number;
}) {
  return useQuery({
    queryKey: [...KEY, 'purchases', params.dates, params.businessId, params.page],
    queryFn: () =>
      post<PurchaseReport>('giftcards/purchase/report', {
        Page: params.page,
        Size: PAGE_SIZE,
        BusinessId: params.businessId,
        // The export path is a separate call; this one always wants a page.
        ExportAll: false,
        ...range(params.dates),
      }),
    staleTime: 30_000,
  });
}

/**
 * One card's header, fresh.
 *
 * Keyed by **code**, not id, despite the route reading `balance/{giftCardId}`
 * and the swagger saying the same: `GetGiftCardAsync` matches on `Code` and
 * ignores the encoder, while every sibling route on this controller decodes an
 * id. Passing an id here returns "gift card not found", so the drawer carries
 * the code as well as the id.
 */
export function useGiftCardByCode(code: string | null) {
  return useQuery({
    queryKey: [...KEY, 'card', code],
    queryFn: () => get<GiftCardLite>(`giftcards/balance/${code}`),
    enabled: !!code,
  });
}

/** One card's own ledger, for the detail drawer. */
export function useCardHistory(giftCardId: string | null, page: number) {
  return useQuery({
    queryKey: [...KEY, 'card-history', giftCardId, page],
    queryFn: () =>
      post<CardHistoryResult>(`giftcards/history/${giftCardId}`, {
        GiftCardId: giftCardId,
        Page: page,
        Size: PAGE_SIZE,
      }),
    enabled: giftCardId !== null,
  });
}

/** Anything that moves money or ownership invalidates every list. */
function useInvalidate() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KEY });
}

/**
 * Mint a batch of codes.
 *
 * The odd one out: on success the endpoint answers with a `codes.txt`
 * attachment rather than the usual envelope, so it cannot go through
 * `unwrap()`. On *failure* it still answers JSON with HTTP 200, which is what
 * `downloadResponse` exists to catch — see `lib/download`.
 */
export function useCreateGiftCards() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: async (total: number) => {
      const text = await downloadResponse(
        api.post('giftcards/create', { Total: total }, { responseType: 'blob' }),
        `gift-card-codes-${new Date().toISOString().slice(0, 10)}.txt`
      );
      return text
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    },
    onSuccess: invalidate,
  });
}

export function useSetNickname() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, nickname }: { id: string; nickname: string }) =>
      post<unknown>(`giftcards/nickname/${id}`, { NewNickname: nickname }),
    onSuccess: invalidate,
  });
}

export function useAttachOwner() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({ id, userId }: { id: string; userId: string }) =>
      post<unknown>(`giftcards/owner/${id}`, { UserId: userId }),
    onSuccess: invalidate,
  });
}

export function useRemoveOwner() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) => post<unknown>(`giftcards/owner/remove/${id}`),
    onSuccess: invalidate,
  });
}

/** Rebuilds a card's balance from its transactions, for when they disagree. */
export function useRecalculateBalance() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (id: string) =>
      post<unknown>(`giftcards/recalc/balance/${id}`, null),
    onSuccess: invalidate,
  });
}

/**
 * Undo one transaction. SuperAdmin or CustomerSupportAdmin only — the service
 * checks, and answers "not enough privileges" to anyone else, so the button is
 * gated on `canReverseGiftCards` rather than shown and left to fail.
 */
export function useRevertTransaction() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: (transactionId: string) =>
      post<unknown>(`giftcards/revert/${transactionId}`),
    onSuccess: invalidate,
  });
}

/** Move a balance onto a replacement card. Same two roles as revert. */
export function useTransferBalance() {
  const invalidate = useInvalidate();

  return useMutation({
    mutationFn: ({
      oldCardCode,
      newCardCode,
    }: {
      oldCardCode: string;
      newCardCode: string;
    }) =>
      post<unknown>('giftcards/transfer', {
        OldCardCode: oldCardCode,
        NewCardCode: newCardCode,
      }),
    onSuccess: invalidate,
  });
}
